import { useState } from 'react'
import { toast } from 'sonner'
import { BookOpen, Copy, Pencil, Plus, Trash2 } from 'lucide-react'
import {
  MAX_PROMPT_BODY_CHARS,
  MAX_PROMPTS,
  useSavedPrompts,
  type SavedPrompt,
} from '@/lib/saved-prompts'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { EmptyState } from '@/components/ui/empty-state'
import { Input, Textarea } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

/**
 * Library of named reusable prompt snippets. The user keeps a list of
 * "saved prompts" (e.g. "Use formal tone", "Skip optional dates") and
 * pastes them into the chat via the picker without retyping. We store
 * them in localStorage — saved prompts are a UX convenience, not a
 * compliance artefact, so they don't belong in the audit log.
 *
 * The component itself is a manager dialog: list / create / edit /
 * delete. The compact picker that consumes the same library lives in
 * ``SavedPromptsPicker``.
 */
export function SavedPromptsManager({
  triggerLabel,
}: {
  triggerLabel?: string
}) {
  const { prompts, isAtCapacity, save, update, remove } = useSavedPrompts()
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<SavedPrompt | null>(null)
  const [draftName, setDraftName] = useState('')
  const [draftBody, setDraftBody] = useState('')
  const [confirmingDelete, setConfirmingDelete] = useState<SavedPrompt | null>(
    null,
  )

  const resetDraft = () => {
    setEditing(null)
    setDraftName('')
    setDraftBody('')
  }

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) {
      resetDraft()
      setConfirmingDelete(null)
    }
  }

  const onStartCreate = () => {
    setEditing(null)
    setDraftName('')
    setDraftBody('')
  }

  const onStartEdit = (p: SavedPrompt) => {
    setEditing(p)
    setDraftName(p.name)
    setDraftBody(p.body)
  }

  const onSave = () => {
    try {
      if (editing) {
        update(editing.id, { name: draftName, body: draftBody })
        toast.success(`Updated "${draftName.trim()}"`)
      } else {
        save({ name: draftName, body: draftBody })
        toast.success(`Saved "${draftName.trim()}"`)
      }
      resetDraft()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Save failed')
    }
  }

  const onConfirmDelete = () => {
    if (!confirmingDelete) return
    remove(confirmingDelete.id)
    toast.success(`Deleted "${confirmingDelete.name}"`)
    if (editing?.id === confirmingDelete.id) resetDraft()
    setConfirmingDelete(null)
  }

  const tooLong = draftBody.length > MAX_PROMPT_BODY_CHARS
  const dirty =
    editing == null
      ? draftName.trim() !== '' || draftBody.trim() !== ''
      : draftName !== editing.name || draftBody !== editing.body
  const canSave =
    draftName.trim().length > 0 && draftBody.trim().length > 0 && !tooLong && dirty
  const showCapacityWarning = isAtCapacity && editing == null

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          data-testid="saved-prompts-trigger"
        >
          <BookOpen className="size-4" aria-hidden />
          {triggerLabel ?? `Saved prompts (${prompts.length})`}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Saved prompts</DialogTitle>
          <DialogDescription>
            Reusable snippets you can paste into the chat or context fields.
            Saved on this device — not synced or sent to the backend.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
          <PromptList
            prompts={prompts}
            activeId={editing?.id ?? null}
            onSelect={onStartEdit}
            onAskDelete={(p) => setConfirmingDelete(p)}
            onStartCreate={onStartCreate}
            isAtCapacity={isAtCapacity}
          />
          <PromptEditor
            editing={editing}
            draftName={draftName}
            draftBody={draftBody}
            onChangeName={setDraftName}
            onChangeBody={setDraftBody}
            onSave={onSave}
            canSave={canSave}
            tooLong={tooLong}
            showCapacityWarning={showCapacityWarning}
          />
        </div>

        {confirmingDelete && (
          <DeleteConfirmation
            prompt={confirmingDelete}
            onCancel={() => setConfirmingDelete(null)}
            onConfirm={onConfirmDelete}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

function PromptList({
  prompts,
  activeId,
  onSelect,
  onAskDelete,
  onStartCreate,
  isAtCapacity,
}: {
  prompts: SavedPrompt[]
  activeId: string | null
  onSelect: (prompt: SavedPrompt) => void
  onAskDelete: (prompt: SavedPrompt) => void
  onStartCreate: () => void
  isAtCapacity: boolean
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">
          {prompts.length} / {MAX_PROMPTS}
        </Label>
        <Button
          variant="outline"
          size="sm"
          onClick={onStartCreate}
          disabled={isAtCapacity}
          data-testid="saved-prompts-new"
        >
          <Plus className="size-3.5" />
          New
        </Button>
      </div>
      {prompts.length === 0 ? (
        <EmptyState
          variant="bare"
          title="No saved prompts yet"
          description="Create one to reuse it across uploads."
        />
      ) : (
        <ul
          className="max-h-72 space-y-1 overflow-y-auto rounded-md border border-border bg-card/60 p-1"
          data-testid="saved-prompts-list"
        >
          {prompts.map((p) => (
            <li key={p.id}>
              <div
                className={
                  activeId === p.id
                    ? 'flex items-start gap-1 rounded-md bg-primary-soft p-1 text-primary-soft-foreground'
                    : 'flex items-start gap-1 rounded-md p-1 hover:bg-accent'
                }
                data-testid={`saved-prompt-row-${p.id}`}
              >
                <button
                  type="button"
                  onClick={() => onSelect(p)}
                  className="flex-1 truncate rounded px-1 py-0.5 text-left text-xs"
                  data-testid={`saved-prompt-select-${p.id}`}
                >
                  <div className="truncate font-medium text-foreground">
                    {p.name}
                  </div>
                  <p className="truncate text-[11px] text-muted-foreground">
                    {p.body}
                  </p>
                </button>
                <button
                  type="button"
                  aria-label={`Delete ${p.name}`}
                  onClick={() => onAskDelete(p)}
                  className="rounded p-0.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                  data-testid={`saved-prompt-delete-${p.id}`}
                >
                  <Trash2 className="size-3" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function PromptEditor({
  editing,
  draftName,
  draftBody,
  onChangeName,
  onChangeBody,
  onSave,
  canSave,
  tooLong,
  showCapacityWarning,
}: {
  editing: SavedPrompt | null
  draftName: string
  draftBody: string
  onChangeName: (next: string) => void
  onChangeBody: (next: string) => void
  onSave: () => void
  canSave: boolean
  tooLong: boolean
  showCapacityWarning: boolean
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">
          {editing ? (
            <span className="inline-flex items-center gap-1">
              <Pencil className="size-3" aria-hidden /> Editing prompt
            </span>
          ) : (
            <span className="inline-flex items-center gap-1">
              <Plus className="size-3" aria-hidden /> New prompt
            </span>
          )}
        </Label>
      </div>
      <Input
        placeholder="Name"
        value={draftName}
        onChange={(e) => onChangeName(e.target.value)}
        data-testid="saved-prompts-name"
        disabled={showCapacityWarning}
      />
      <Textarea
        placeholder="Body"
        rows={6}
        value={draftBody}
        onChange={(e) => onChangeBody(e.target.value)}
        data-testid="saved-prompts-body"
        disabled={showCapacityWarning}
        className="resize-y"
      />
      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
        <span data-testid="saved-prompts-body-count">
          {draftBody.length.toLocaleString()} /{' '}
          {MAX_PROMPT_BODY_CHARS.toLocaleString()} chars
        </span>
        {tooLong && (
          <Badge tone="danger" size="xs">
            Too long
          </Badge>
        )}
      </div>
      {showCapacityWarning && (
        <p
          className="text-xs leading-relaxed text-destructive"
          data-testid="saved-prompts-capacity-warning"
        >
          Saved-prompt limit reached ({MAX_PROMPTS}). Delete an entry to add a
          new one.
        </p>
      )}
      <Button
        type="button"
        onClick={onSave}
        disabled={!canSave}
        data-testid="saved-prompts-save"
        className="w-full sm:w-auto"
      >
        {editing ? 'Update prompt' : 'Save prompt'}
      </Button>
    </div>
  )
}

function DeleteConfirmation({
  prompt,
  onCancel,
  onConfirm,
}: {
  prompt: SavedPrompt
  onCancel: () => void
  onConfirm: () => void
}) {
  return (
    <div
      className="rounded-md border border-destructive/40 bg-destructive-soft/40 p-3 text-xs leading-relaxed text-destructive-soft-foreground"
      role="alertdialog"
      aria-label="Confirm delete saved prompt"
      data-testid="saved-prompts-delete-confirm"
    >
      <p>
        Delete <span className="font-semibold">{prompt.name}</span>? This
        prompt is stored on this device only — deletion cannot be undone from
        another device.
      </p>
      <DialogFooter className="mt-3">
        <Button
          variant="outline"
          size="sm"
          onClick={onCancel}
          data-testid="saved-prompts-delete-cancel"
        >
          Cancel
        </Button>
        <Button
          variant="destructive"
          size="sm"
          onClick={onConfirm}
          data-testid="saved-prompts-delete-confirm-button"
        >
          <Trash2 className="size-3.5" />
          Delete
        </Button>
      </DialogFooter>
    </div>
  )
}

/**
 * Compact button + popover combo: surfaces the saved-prompts library next
 * to a textarea so the user can drop a snippet into the field.
 */
export function SavedPromptsPicker({
  onInsert,
  triggerLabel = 'Insert saved prompt',
  className,
}: {
  onInsert: (body: string) => void
  triggerLabel?: string
  className?: string
}) {
  const { prompts } = useSavedPrompts()
  const [open, setOpen] = useState(false)

  if (prompts.length === 0) return null

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={className}
          data-testid="saved-prompts-picker-trigger"
        >
          <Copy className="size-3.5" aria-hidden />
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Insert a saved prompt</DialogTitle>
          <DialogDescription>
            Pick one of your reusable snippets to paste into the field.
          </DialogDescription>
        </DialogHeader>
        <ul
          className="max-h-72 space-y-1 overflow-y-auto"
          data-testid="saved-prompts-picker-list"
        >
          {prompts.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => {
                  onInsert(p.body)
                  setOpen(false)
                }}
                className="w-full rounded-md border border-border bg-card px-3 py-2 text-left text-xs hover:border-primary/40 hover:bg-primary-soft/40"
                data-testid={`saved-prompts-picker-${p.id}`}
              >
                <div className="font-medium text-foreground">{p.name}</div>
                <div className="line-clamp-2 text-muted-foreground">
                  {p.body}
                </div>
              </button>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  )
}
